import { useEffect, useState } from 'react';

export interface FormNumber {
	domain: string;
	nickname: string;
	api: string;
	token: string;
	device_id: string;
	waba_account_id: string;
	namespace: string;
	country_code: string;
	country_code_dial: string;
	remove_cc: string;
	number: string;
	status: string;
	picture_url?: string;
}

declare global {
	interface Window {
		fbAsyncInit: () => void;
		FB: any;
	}
}

let sdkPromise: Promise<void> | null = null;
let sdkInitialized = false;

// Um SDK por pagina. Quem precisa abrir o popup sem esperar a rede (onboarding) chama isto antes.
export function loadFacebookSdk(): Promise<void> {
	if (!sdkPromise) {
		sdkPromise = new Promise((resolve, reject) => {
			window.fbAsyncInit = () => {
				window.FB.init({
					appId: process.env.REACT_APP_META_CLIENT_ID,
					autoLogAppEvents: true,
					xfbml: true,
					version: process.env.REACT_APP_META_VERSION,
				});
				sdkInitialized = true;
				resolve();
			};

			const script = document.createElement('script');
			script.src = 'https://connect.facebook.net/en_US/sdk.js';
			script.async = true;
			script.defer = true;
			script.crossOrigin = 'anonymous';
			script.onerror = () => {
				sdkPromise = null;
				reject(new Error('Facebook SDK não carregou'));
			};
			document.body.appendChild(script);
		});
	}
	return sdkPromise;
}

const getBusinessToken = async (code: string): Promise<string> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_OFICIAL_URL}/whatsapp/official/business-token`,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ code }),
			},
		);

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`Erro na requisição: ${response.status} - ${errorText}`);
		}

		const data = await response.json();
		return data.accessToken;
	} catch (error: any) {
		console.error('Erro ao obter token do backend:', error?.message || error);
		throw error;
	}
};

const getPhoneNumber = async (
	data: FormNumber,
): Promise<{
	display_phone_number: string;
	verified_name: string;
	phone_number_id: string;
	picture_url?: string;
}> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_OFICIAL_URL}/whatsapp/official/phone-number`,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					wabaId: data.waba_account_id,
					phoneNumberId: data.device_id,
					accountToken: data.token,
				}),
			},
		);

		if (!response.ok) {
			throw new Error('Erro na requisição');
		}

		return await response.json();
	} catch (error: any) {
		console.error('Erro ao buscar número no backend:', error?.message || error);
		throw error;
	}
};

// Embedded Signup da Meta. `onFinish` pode devolver `false` (ou uma Promise de `false`)
// para dizer que o cadastro do numero falhou depois da Meta - isso vira `failure` = 'account'.
export function useMetaSignup(
	formNumber: FormNumber,
	onFinish: (formNumber: FormNumber) => unknown,
) {
	const [whatsAppData, setWhatsAppData] = useState<FormNumber>(formNumber);
	const [loading, setLoading] = useState<boolean>(false);
	const [embeddedSignup, setEmbeddedSignup] = useState(false);
	const [sdkReady, setSdkReady] = useState(sdkInitialized);
	// 'interrupted': janela fechada/cancelada; 'account': Meta, token, numero ou cadastro falharam.
	const [failure, setFailure] = useState<'interrupted' | 'account' | null>(null);

	const fail = (reason: 'interrupted' | 'account') => {
		setLoading(false);
		setFailure(reason);
	};

	useEffect(() => {
		loadFacebookSdk()
			.then(() => setSdkReady(true))
			.catch((error) => console.error(error));

		const messageHandler = async (event: MessageEvent) => {
			if (!event.origin.endsWith('facebook.com')) return;

			try {
				const data = JSON.parse(event.data);
				if (data.type === 'WA_EMBEDDED_SIGNUP') {
					if (data.event === 'FINISH') {
						const { phone_number_id, business_id, waba_id } = data.data;

						setLoading(true);
						setWhatsAppData((prev) => ({
							...prev,
							device_id: phone_number_id,
							waba_account_id: waba_id,
							api: 'meta',
						}));
						setEmbeddedSignup(true);
					} else if (data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
						const { business_id, waba_id } = data.data;

						setLoading(true);
						setWhatsAppData((prev) => ({
							...prev,
							waba_account_id: waba_id,
							api: 'meta',
						}));
						setEmbeddedSignup(true);
					} else if (data.event === 'CANCEL') {
						console.warn('Cadastro cancelado em:', data.data?.current_step);
						fail('interrupted');
					} else if (data.event === 'ERROR') {
						console.error('Erro no cadastro:', data.data?.error_message);
						fail('account');
					}
				}
			} catch {
				console.log('Resposta não JSON:', event.data);
			}
		};

		window.addEventListener('message', messageHandler);

		return () => {
			window.removeEventListener('message', messageHandler);
		};
	}, []);

	const fbLoginCallback = (response: any) => {
		if (!response.authResponse) {
			// Janela fechada antes do fim: sem isto a tela fica parada sem nada para clicar.
			fail('interrupted');
			return;
		}
		getBusinessToken(response.authResponse.code)
			.then((businessToken) => {
				setWhatsAppData((prev) => ({
					...prev,
					token: businessToken,
				}));
			})
			.catch((error) => {
				console.error('Erro ao obter token de negócio:', error);
				fail('account');
			});
	};

	const launch = () => {
		if (!window.FB) {
			console.error('Facebook SDK não carregado');
			return;
		}
		setFailure(null);
		window.FB.login(fbLoginCallback, {
			config_id: process.env.REACT_APP_META_CONFIG_ID,
			response_type: 'code',
			override_default_response_type: true,
			extras: {
				setup: {},
				featureType: 'whatsapp_business_app_onboarding',
				sessionInfoVersion: '3',
			},
		});
	};

	useEffect(() => {
		if (whatsAppData.token && whatsAppData.waba_account_id) {
			getPhoneNumber(whatsAppData)
				.then((data) => {
					const updatedWhatsAppData = {
						...whatsAppData,
						device_id: data.phone_number_id,
						number: data.display_phone_number,
						nickname: data.verified_name,
						...(data.picture_url && { picture_url: data.picture_url }),
					};

					setWhatsAppData(updatedWhatsAppData);

					if (embeddedSignup) {
						Promise.resolve(onFinish(updatedWhatsAppData)).then((ok) => {
							if (ok === false) fail('account');
						});
					}
				})
				.catch((error) => {
					console.error('Erro ao buscar o número:', error);
					fail('account');
				});
		}
	}, [whatsAppData.token, whatsAppData.waba_account_id, whatsAppData.device_id]);

	return { whatsAppData, loading, sdkReady, failure, launch };
}
